import React, { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { getTaxCaseById } from '../services/firestoreService';
import { TaxCaseDoc, TaxStatus } from '../types';
import StatusTracker from '../components/StatusTracker';
import CaseDocumentsPanel from '../components/CaseDocumentsPanel';
import CaseDocumentUploader from '../components/CaseDocumentUploader';

interface ClientCaseDetailProps {
  caseId: string;
  firmId: string;
  onBack: () => void;
}

const ClientCaseDetail: React.FC<ClientCaseDetailProps> = ({ caseId, firmId, onBack }) => {
  const { user } = useAuth();
  const [caseData, setCaseData] = useState<TaxCaseDoc | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState<'overview' | 'documents'>('overview');

  useEffect(() => {
    const fetchCase = async () => {
      setLoading(true);
      try {
        const data = await getTaxCaseById(firmId, caseId);
        
        // Ownership Validation Logic
        const isDemoMode = firmId === 'hannah-financial-demo' || caseId.includes('demo');
        
        if (!data) {
          setError('Return not found.');
        } else if (!isDemoMode && user && data.clientId !== user.uid) {
          // Strict check only for production data
          setError('Access Denied: Return ownership mismatch.');
        } else {
          setCaseData(data);
        }
      } catch (err) {
        setError('Failed to load return details.');
      } finally {
        setLoading(false);
      }
    };
    fetchCase();
  }, [caseId, firmId, user]);

  if (loading) return <div className="p-12 text-center text-slate-500">Loading details...</div>;
  if (error) return (
    <div className="p-12 flex flex-col items-center justify-center min-h-[400px]">
        <div className="bg-red-50 p-8 rounded-3xl border border-red-100 text-center max-w-md animate-fade-in">
            <span className="text-4xl mb-4 block">🚫</span>
            <h2 className="text-xl font-black text-red-800 mb-2">Access Restricted</h2>
            <p className="text-red-600 text-sm font-medium mb-6">{error}</p>
            <button onClick={onBack} className="px-6 py-2 bg-white border border-red-200 text-red-600 rounded-xl font-bold hover:bg-red-100 transition-colors">
                Return to Dashboard
            </button>
        </div>
    </div>
  );
  
  if (!caseData || !user) return null;

  return (
    <div className="max-w-5xl mx-auto p-2">
      <button onClick={onBack} className="mb-6 text-slate-500 hover:text-brand-600 flex items-center gap-1 group">
        <span className="group-hover:-translate-x-1 transition-transform">←</span> Back
      </button>

      <div className="flex justify-between items-center mb-8">
        <h1 className="text-3xl font-black text-slate-800">{caseData.year} Tax Return</h1>
        <span className="px-4 py-2 rounded-full font-bold text-sm bg-brand-50 text-brand-800 border border-brand-200">{caseData.status}</span>
      </div>

      <div className="flex border-b border-slate-200 mb-8">
        {['overview', 'documents'].map(tab => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab as any)}
            className={`px-6 py-3 text-sm font-bold border-b-2 transition-colors uppercase tracking-widest ${
              activeTab === tab ? 'border-brand-600 text-brand-600' : 'border-transparent text-slate-400 hover:text-slate-600'
            }`}
          >
            {tab}
          </button>
        ))}
      </div>

      <div className="animate-fade-in">
        {activeTab === 'overview' && (
          <div className="space-y-8">
            <StatusTracker currentStatus={caseData.status as TaxStatus} />
            <div className="bg-white p-8 rounded-3xl border border-slate-200 shadow-sm">
                 <h3 className="font-black text-slate-800 uppercase tracking-widest text-xs mb-6 pb-2 border-b border-slate-100">Summary</h3>
                 <div className="grid grid-cols-1 md:grid-cols-2 gap-8 text-sm">
                    <div className="space-y-4">
                       <div className="flex justify-between font-medium"><span className="text-slate-400">Year</span><span>{caseData.year}</span></div>
                       <div className="flex justify-between font-medium"><span className="text-slate-400">Type</span><span>{caseData.filingType}</span></div>
                    </div>
                    <div className="space-y-4">
                       <div className="flex justify-between font-medium"><span className="text-slate-400">Staff Assigned</span><span>{caseData.assignedStaffName || 'Pending'}</span></div>
                       <div className="flex justify-between font-medium"><span className="text-slate-400">Last Activity</span><span>{caseData.lastUpdated?.toDate?.().toLocaleDateString() || 'Just now'}</span></div>
                    </div>
                 </div>
            </div>
          </div>
        )}
        {activeTab === 'documents' && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2">
              <CaseDocumentsPanel caseId={caseId} isStaff={false} />
            </div>
            <div>
              <CaseDocumentUploader caseId={caseId} currentUserUid={user.uid} />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ClientCaseDetail;